import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import LoadingButton from "@mui/lab/LoadingButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import { ReactComponent as MuiInfoIcon } from "../icons/Info24.svg";
import { getPeriodName } from "../services/SchoolService";
import List from "@mui/material/List";
import PeriodListItem from "../components/PeriodListItem";
import { doService } from "../services/ServiceHelper";
import {
  getCategories,
  getDateActivities,
  getDayForStudent,
  getSelectedActivityFromResult,
  saveStudentDay,
} from "../services/ActivityService";
import clone from "lodash-es/clone";
import { isStringEmpty } from "../services/StringService";
import { getUserPriority } from "../services/UserService";
import { getYYYYMMDD, getMMDD } from "../services/DateService";

const StudentActivitySignupDialog = ({
  open,
  dialogDate,
  school,
  jwt,
  studentEid,
  onClose,
  onSaveComplete,
  userSettings,
}) => {
  const [_selectedCategoryEid, setSelectedCategoryEid] = useState("XX");
  const [_detailActivity, setDetailActivity] = useState({});
  const [_detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [_detailActivityPeriod, setDetailActivityPeriod] = useState(1);
  const [_showProgress, setShowProgress] = useState(false);

  const [_allPeriodActivities, setAllPeriodActivities] = useState({
    period1: [],
    period2: [],
    period3: [],
    period4: [],
    period5: [],
    period6: [],
    period7: [],
    period8: [],
    period9: [],
    period10: [],
  });

  const [_filteredPeriodActivities, setFilteredPeriodActivities] = useState({
    period1: [],
    period2: [],
    period3: [],
    period4: [],
    period5: [],
    period6: [],
    period7: [],
    period8: [],
    period9: [],
    period10: [],
  });


  const getEmptySelectedActivity = () => {
    return { activityDateEid: "XX" };
  };

  const [_originalSelectedActivities, setOriginalSelectedActivities] = useState(
    {
      period1: getEmptySelectedActivity(),
      period2: getEmptySelectedActivity(),
      period3: getEmptySelectedActivity(),
      period4: getEmptySelectedActivity(),
      period5: getEmptySelectedActivity(),
      period6: getEmptySelectedActivity(),
      period7: getEmptySelectedActivity(),
      period8: getEmptySelectedActivity(),
      period9: getEmptySelectedActivity(),
      period10: getEmptySelectedActivity(),
    },
  );

  const [_selectedActivities, setSelectedActivities] = useState({
    period1: getEmptySelectedActivity(),
    period2: getEmptySelectedActivity(),
    period3: getEmptySelectedActivity(),
    period4: getEmptySelectedActivity(),
    period5: getEmptySelectedActivity(),
    period6: getEmptySelectedActivity(),
    period7: getEmptySelectedActivity(),
    period8: getEmptySelectedActivity(),
    period9: getEmptySelectedActivity(),
    period10: getEmptySelectedActivity(),
  });

  const [_categories, setCategories] = useState([]);

  // fired when displayed (i.e., when the dialog is opened)
  useEffect(() => {
    if (!open || studentEid == null) {
      return;
    }

    clearAllPeriodActivities();

    let promises = [
      getDateActivities(school, jwt, dialogDate, studentEid),
      getDayForStudent(school, jwt, dialogDate, studentEid),
    ];

    Promise.all(promises)
      .then((results) => {
        setAllPeriodActivities(results[0].data);
        setFilteredPeriodActivities(results[0].data);
        loadStudentSelectedActivities(results[1].data.activities);
        // would normally set display now
      })
      .finally(() => {
        // show progress
      });
  }, [open, studentEid]);

  useEffect(() => {
    initCategories();
  }, [school, jwt]);

  useEffect(() => {
    setSelectedCategoryEid("XX");
  }, [open]);


  const loadStudentSelectedActivities = (activities) => {
    const selectedActivities = clone(_selectedActivities);

    for (let periodNum = 1; periodNum <= school.maxActivityCount; periodNum++) {
      const key = `period${periodNum}`;
      let value = getEmptySelectedActivity();

      // initialize value
      selectedActivities[key] = value;

      // if key set then get value
      if (key in activities) {
        _selectedActivities[key] = activities[key];
        value = getSelectedActivityFromResult(activities, periodNum);
        if (value == null) {
          value = getEmptySelectedActivity();
        }
      }
      selectedActivities[key] = value;
    }

    setSelectedActivities(selectedActivities);
    setOriginalSelectedActivities(clone(selectedActivities));
  };

  const clearAllPeriodActivities = () => {
    setAllPeriodActivities({
      period1: [],
      period2: [],
      period3: [],
      period4: [],
      period5: [],
      period6: [],
      period7: [],
      period8: [],
      period9: [],
      period10: [],
    });
  };

  const initCategories = () => {
    doService(getCategories(school, jwt), (results) => {
      setCategories(results.data);
    });
  };

  const handleChangeCategory = (event) => {
    let categoryEid = "XX";
    if (event != null) {
      categoryEid = event.target.value;
    }
    setSelectedCategoryEid(categoryEid);

    const category = getSelectedCategory(categoryEid);

    // pass both, in case category is null (e.g. 'XX')
    filterByCategory(categoryEid, category);
  };

  const getSelectedCategory = (eid) => {
    for (let category of _categories) {
      if (category.eid === eid) {
        return category;
      }
    }
    return null;
  };

  const filterByCategory = (categoryEid, category) => {
    // TODO FIX WITH PERIOD KEYS, THEN THE ACTIVITIES
    const filteredActivities = {};

    // create the buckets for the the number of periods being shown for filtration
    let periodCount = 0;
    for (let i = 1; i <= 10; i++) {
      const key = `period${i}`;

      // stop looping once we hit the max number of activities
      if (_allPeriodActivities[key] == null) {
        break;
      }

      // assign the key
      filteredActivities[key] = [];
      periodCount++;
    }

    // perform the filtration, for every period for the day
    for (let i = 1; i <= periodCount; i++) {
      const periodKey = `period${i}`;

      // loop each period
      for (let activity of _allPeriodActivities[periodKey]) {
        // add if "all categories is selected"
        // OR category ID matches activity id
        if (
          categoryEid === "XX" ||
          (category != null && category.id === activity.categoryId)
        ) {
          filteredActivities[periodKey].push(activity);
        } else {
          // OR there's a preselected activity id (regardless of category)
          if (
            _selectedActivities[periodKey].activityDateEid ===
            activity.activityDateEid
          ) {
            filteredActivities[periodKey].push(activity);
          }
        }
      }
    }

    setFilteredPeriodActivities(filteredActivities);
  };

  const handleDetailClick = (activity, periodNum) => {
    if (activity.activityDateEid === "XX") {
      window.showAlert("Unable to Show Detail", "Please select an activity.");
      return;
    }

    // if there's no ID on the activity object, then the student has casually selected
    // an activity, but has not clicked the "Sign up" button yet.  We need to get the info about the activity from another source.
    setDetailActivityPeriod(periodNum);
    if (activity.id != null) {
      setDetailActivity(activity);
    } else {
      for (let a of _allPeriodActivities[`period${periodNum}`]) {
        if (activity.activityDateEid === a.activityDateEid) {
          setDetailActivity(a);
          break;
        }
      }
    }

    setDetailDialogOpen(true);
  };

  const handleDetailDialogClose = () => {
    setDetailDialogOpen(false);
  };

  const getActivityEntry = (periodNum, activityDateEid) => {
    for (let a of _allPeriodActivities[`period${periodNum}`]) {
      if (activityDateEid === a.activityDateEid) {
        return a;
      }
    }

    return null;
  };

  const getOriginalSelectedActivityEntry = (periodNum) => {
    return _originalSelectedActivities[`period${periodNum}`];
  };

  const handleChangeSelectedActivity = (value, periodNum) => {
    // scrape out mav and get the period number

    // clone and set
    const selectedActivities = clone(_selectedActivities);
    selectedActivities[`period${periodNum}`] = { activityDateEid: value };
    setSelectedActivities(selectedActivities);
  };

  const handleDialogSave = () => {
    const values = {
      date: getYYYYMMDD(dialogDate),
      attendanceType: "I",
    };
    const facultyPriority = getUserPriority(jwt);

    if (studentEid != null) {
      values["studentEid"] = studentEid;
    }

    // loop number of periods for today
    for (let periodNum = 1; periodNum <= school.maxActivityCount; periodNum++) {
      // key: value
      let selectedActivity = _selectedActivities[`period${periodNum}`];
      let activityDateEid = selectedActivity.activityDateEid;

      let activityDetails = getActivityEntry(periodNum, activityDateEid);

      // if already maxxed out then show message;
      // see if the activity has been changed from original selections
      let originalSelectedActivity =
        getOriginalSelectedActivityEntry(periodNum);

      let originalSelectedActivityDateEid = null;
      let selectedActivityDateEid = null;

      if (originalSelectedActivity != null) {
        originalSelectedActivityDateEid =
          originalSelectedActivity.activityDateEid;
      }
      if (activityDetails != null) {
        selectedActivityDateEid = activityDetails.activityDateEid;
      }

      // if there was a change then see if its maxxed out
      if (originalSelectedActivityDateEid !== selectedActivityDateEid) {
        // if empty and not admin show message
        if (
          activityDetails != null &&
          activityDetails.left === 0 &&
          facultyPriority !== 800
        ) {
          window.showAlert(
            "Unable to Save Activity",
            `For ${getPeriodName(school, periodNum - 1)}, "${
              activityDetails.name
            }" is MAXXED OUT.  Please select another activity.`,
          );
          return;
        }
      }

      // teachers cannot deselect an activity
      // if (activityDateEid === "XX" && facultyPriority < 800) {
      //   window.showAlert(
      //     "Unable to Save Activity",
      //     `Student must be assigned an activity for ${getPeriodName(_school, periodNum - 1)}.`,
      //   );
      //   return;
      // }

      if (activityDateEid === "XX") {
        activityDateEid = "";
      }

      // assign the activity date eid
      values[`mav${periodNum}`] = activityDateEid;
      values[`mavreason${periodNum}`] = "";

      if (userSettings.markStudentCanModify === "N") {
        values[`mavnotmodify${periodNum}`] = true;
      } else {
        values[`mavnotmodify${periodNum}`] = false;
      }

      values[`mavcomment${periodNum}`] = "";
      values[`favorite${periodNum}`] = false;
    }

    setShowProgress(true);
    saveStudentDay(school, jwt, values)
      .then((result) => {
        // old service
        if (!isStringEmpty(result.message)) {
          window.showAlert("Unable to Save Date", result.data.message);
          return;
        }

        if (!isStringEmpty(result.data)) {
          let message = "" + result.data;
          message = message.trim();
          window.showAlert("Unable to Save Date", message);
          return;
        }

        // new service
        if (result.detail) {
          window.showAlert("Unable to Save Activity", result.detail);
          return;
        }

        window.showSnack(`Activities Saved for ${getMMDD(dialogDate)}.`);
        if (onSaveComplete) {
          onSaveComplete(result.data);
          return;
        }
      })
      .catch((error) => {
        window.showSnack(
          "Unable to Save Activities for " + getYYYYMMDD(dialogDate) + ".",
        );
        console.log(error);
      })
      .finally(() => {
        setShowProgress(false);
      });
  };

  const isSignupButtonDisabled = () => {
    // _selectedActivities["period" + (i + 1)].modify === 0
    // if all drop boxes are disabled then return true

    let modifyCount = 0;
    for (const [key, a] of Object.entries(_selectedActivities)) {
      if (a.modify === 0) {
        modifyCount++;
      }
    }

    if (modifyCount >= school.maxActivityCount) {
      return true;
    }

    return false;
  };

  return (
    <>
      <Dialog fullWidth={true} open={open} onClose={onClose}>
        <DialogTitle sx={{ p: 1 }}>For {getMMDD(dialogDate)}: </DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          {school.groupStudentActivities === "Y" && (
            <>
              <strong style={{ marginBottom: "10px" }}>Select Category:</strong>
              <Select
                onChange={handleChangeCategory}
                value={_selectedCategoryEid}
                sx={{ mb: 1 }}
                fullWidth
                size="small"
              >
                <MenuItem key="XX" value="XX">
                  -- ALL CATEGORIES --
                </MenuItem>
                {_categories.map((category) => (
                  <MenuItem key={category.eid} value={category.eid}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
              <br />
              <br />
            </>
          )}
          {[...Array(school.maxActivityCount)].map((x, i) => (
            <Grid key={"pg" + i} container sx={{ mb: 1 }}>
              <Grid item xs={10} sx={{ pr: 0 }}>
                <strong>{getPeriodName(school, i)}:</strong>
                <br />
                <Select
                  id={"mav" + (i + 1)}
                  onChange={(event) =>
                    handleChangeSelectedActivity(event.target.value, i + 1)
                  }
                  value={
                    _selectedActivities["period" + (i + 1)].activityDateEid
                  }
                  sx={{ mb: 1 }}
                  fullWidth
                  size="small"
                >
                  <MenuItem key="XX" value="XX">
                    -- NO ACTIVITY SELECTED --
                  </MenuItem>
                  {_filteredPeriodActivities["period" + (i + 1)].map(
                    (activity) => (
                      <MenuItem
                        key={activity.activityDateEid}
                        value={activity.activityDateEid}
                      >
                        {activity.last_name} - {activity.name}
                        {activity.left === 0 && <>&nbsp;(MAXXED OUT)</>}
                        {activity.left > 0 && (
                          <>- {activity.left} seat(s) left</>
                        )}
                      </MenuItem>
                    ),
                  )}
                </Select>
              </Grid>
              <Grid item xs={2}>
                <br />
                <IconButton
                  variant="contained"
                  aria-label="info"
                  aria-details="info"
                  color="primary"
                  onClick={() =>
                    handleDetailClick(
                      _selectedActivities["period" + (i + 1)],
                      i + 1,
                    )
                  }
                >
                  <MuiInfoIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <LoadingButton
            sx={{ width: 120 }}
            onClick={handleDialogSave}
            loading={_showProgress}
            variant="contained"
            disabled={isSignupButtonDisabled()}
          >
            Sign Up
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Dialog
        fullWidth={true}
        open={_detailDialogOpen}
        onClose={handleDetailDialogClose}
      >
        <DialogTitle sx={{ p: 1 }}>Activity Details</DialogTitle>
        <DialogContent sx={{ pb: 0 }}>
          <List sx={{ width: "100%" }}>
            <PeriodListItem
              school={school}
              periodNumber={_detailActivityPeriod}
              period={_detailActivity}
            />
          </List>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleDetailDialogClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default StudentActivitySignupDialog;
